import React, { useState, useEffect, useCallback } from 'react';
import { Modal, message } from 'antd';
import { FC112 } from '../../../services/scApi';
import { scApi } from '../../../services/scApi';
import { fmt } from '../helpers';

interface ExpenseDetailModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (expenses: Partial<FC112>[]) => void;
  expenses: Partial<FC112>[];
  scNo: string;
  itemSeq: number;
}

const ExpenseDetailModal: React.FC<ExpenseDetailModalProps> = ({
  open, onClose, onSave, expenses, scNo, itemSeq,
}) => {
  const [rows, setRows] = useState<Partial<FC112>[]>([]);
  const [expenseNames, setExpenseNames] = useState<Record<string, string>>({});

  // Reset rows when modal opens
  useEffect(() => {
    if (open) {
      const init = expenses.length > 0
        ? expenses.map(c => ({ ...c }))
        : [];
      setRows(init);
      // Resolve expense names for existing rows
      const namesMap: Record<string, string> = {};
      const lookups = init
        .filter(r => r.fc112_11)
        .map(r =>
          scApi.lookupName('expenseCode', r.fc112_11!).then(name => {
            namesMap[r.fc112_11!] = name;
          })
        );
      Promise.all(lookups).then(() => setExpenseNames(namesMap));
    }
  }, [open, expenses]);

  const addRow = useCallback(() => {
    const maxSeq = rows.reduce((max, r) => Math.max(max, r.fc112_03 || 0), 0);
    setRows(prev => [
      ...prev,
      {
        fc112_01: scNo,
        fc112_02: itemSeq,
        fc112_03: maxSeq + 1,
        fc112_11: '',
        fc112_12: 0,
      },
    ]);
  }, [rows, scNo, itemSeq]);

  const deleteRow = useCallback((idx: number) => {
    setRows(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((r, i) => ({ ...r, fc112_03: i + 1 }));
    });
  }, []);

  const updateField = useCallback((idx: number, key: keyof FC112, value: FC112[keyof FC112]) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  }, []);

  const handleExpenseBlur = useCallback((idx: number, code: string) => {
    if (!code.trim()) return;
    scApi.lookupName('expenseCode', code.trim()).then(name => {
      setExpenseNames(prev => ({ ...prev, [code.trim()]: name }));
    });
  }, []);

  // Compute total expense
  const totalExpense = rows.reduce((sum, r) => sum + (r.fc112_12 || 0), 0);

  const handleOk = () => {
    // Each row: expense code required if amount is non-zero
    for (let i = 0; i < rows.length; i++) {
      const code = (rows[i].fc112_11 || '').trim();
      const amount = rows[i].fc112_12 || 0;
      if (amount !== 0 && !code) {
        message.error(`第 ${i + 1} 行：金額非零時費用代號不可空白`);
        return;
      }
      if (code && !expenseNames[code]) {
        message.error(`第 ${i + 1} 行：查無此費用代號!`);
        return;
      }
    }

    onSave(rows);
  };

  return (
    <Modal
      title={`費用明細 - 項次 ${itemSeq}`}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      width={600}
      okText="確認"
      cancelText="取消"
      destroyOnClose
    >
      <div style={{ marginBottom: 8 }}>
        <button
          className="btn btn-success btn-sm"
          onClick={addRow}
          style={{ marginRight: 8 }}
        >
          + 新增費用
        </button>
      </div>

      <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ width: 40, padding: '6px 8px', textAlign: 'center', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>序</th>
            <th style={{ width: 100, padding: '6px 8px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>費用代號</th>
            <th style={{ padding: '6px 8px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>費用名稱</th>
            <th style={{ width: 120, padding: '6px 8px', textAlign: 'right', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>金額</th>
            <th style={{ width: 50, padding: '6px 8px', textAlign: 'center', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: 'center', padding: '16px 8px', color: '#999' }}>
                尚無費用明細，請點擊「+ 新增費用」
              </td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                {r.fc112_03 || i + 1}
              </td>
              <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>
                <input
                  type="text"
                  style={{ width: '100%', height: 28, fontSize: 13, border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 8px' }}
                  value={r.fc112_11 || ''}
                  onChange={e => updateField(i, 'fc112_11', e.target.value)}
                  onBlur={e => handleExpenseBlur(i, e.target.value)}
                />
              </td>
              <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
                {(() => {
                  const code = (r.fc112_11 || '').trim();
                  const name = expenseNames[code];
                  if (!code) return <span style={{ color: '#666' }}>--</span>;
                  if (name) return <span style={{ color: '#666' }}>{name}</span>;
                  if (code in expenseNames) return <span style={{ color: '#ff4d4f' }}>*查無此代號*</span>;
                  return <span style={{ color: '#666' }}>--</span>;
                })()}
              </td>
              <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>
                <input
                  type="number"
                  style={{ width: '100%', height: 28, fontSize: 13, textAlign: 'right', border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 8px' }}
                  value={r.fc112_12 || ''}
                  onChange={e => updateField(i, 'fc112_12', parseFloat(e.target.value) || 0)}
                />
              </td>
              <td style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                <button
                  className="btn btn-danger btn-sm"
                  style={{ height: 24, padding: '0 8px', fontSize: 12 }}
                  onClick={() => deleteRow(i)}
                >
                  刪除
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ padding: '8px', textAlign: 'right', fontWeight: 600, fontSize: 13, borderTop: '2px solid #f0f0f0' }}>
              費用合計:
            </td>
            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, fontSize: 13, borderTop: '2px solid #f0f0f0' }}>
              {fmt(totalExpense)}
            </td>
            <td style={{ borderTop: '2px solid #f0f0f0' }}></td>
          </tr>
        </tfoot>
      </table>
    </Modal>
  );
};

export default React.memo(ExpenseDetailModal);
